import {toMediumDate} from '@pinecast/common/dates';
import Group, {VerticalGroup} from '@pinecast/common/Group';
import {formatCurrency} from '@pinecast/common/helpers/format';
import {CalendarScheduled} from '@pinecast/common/icons';
import type {SubscriptionsPage_tipJarSubscriptions} from '@pinecast/gql/__generated_tj__/SubscriptionsPage';
import {defineMessages, FormattedMessage} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';
import * as numeral from 'numeral';
import {Link} from 'react-router-dom';

const messages = defineMessages({
  donating: {
    id: 'tj-spa.SubscriptionsPageRecurringDonationPodcast.donating',
    description:
      "Description of outstanding donations to a podcast. {amount} is a dollar amount for the donation. {day} is the day of the month that the donation lands (e.g., '4th')",
    defaultMessage: 'You tip {amount} on the {day} of every month.',
  },
  since: {
    id: 'tj-spa.SubscriptionsPageRecurringDonationPodcast.since',
    description:
      'Note about how long a recurring donation has been ongoing for. {date} is the date that the subscription started',
    defaultMessage: 'Since {date}',
  },
  last: {
    id: 'tj-spa.SubscriptionsPageRecurringDonationPodcast.last',
    description:
      'When the last payment for a subscription was. {date} is the date of the last payment',
    defaultMessage: 'Last billed on {date}',
  },
});

type Props = {
  subscription: SubscriptionsPage_tipJarSubscriptions;
};

const SubscriptionsPageRecurringDonationPodcast = ({subscription}: Props) => {
  const css = useCSS();
  const designColors = useColors();

  const billedSince = toMediumDate(subscription.created);
  const lastBilled = toMediumDate(subscription.lastEvent.occurredAt);

  return (
    <div
      className={css(
        {
          display: 'grid',
          gridTemplateColumns: '40px 1fr 24px',
          gridColumnGap: '12px',
          ':not(:empty) + .SubscriptionsPageRecurringDonationPodcast': {
            marginTop: '28px',
          },
        },
        'SubscriptionsPageRecurringDonationPodcast',
      )}
    >
      <img
        className={css({
          borderRadius: '4px',
          display: 'block',
          height: '40px',
          width: '40px',
        })}
        src={subscription.podcast.coverArtThumbnail}
        alt=""
        aria-hidden="true"
      />

      <Link
        to={`/subscriptions/recurring_tips/${subscription.podcast.slug}`}
        className={css({
          color: designColors.contentText,
          display: 'block',
          textDecoration: 'none',
          ':hover .SubscriptionsPageRecurringDonationPodcast-PodcastTitle': {
            textDecoration: 'underline',
          },
        })}
      >
        <VerticalGroup spacing={4}>
          <strong
            className={css(
              {fontSize: '18px'},
              'SubscriptionsPageRecurringDonationPodcast-PodcastTitle',
            )}
          >
            {subscription.podcast.name}
          </strong>
          <p
            className={css({
              fontSize: '14px',
              marginBottom: '0',
              marginTop: '0',
            })}
          >
            <FormattedMessage
              {...messages.donating}
              values={{
                amount: formatCurrency(
                  subscription.amount,
                  subscription.currency,
                ),
                day: numeral(new Date(subscription.created).getDate()).format(
                  '0o',
                ),
              }}
            />
          </p>
          <Group
            centerChildren
            spacing={4}
            wrapperStyle={{color: designColors.headingBlue}}
          >
            <CalendarScheduled />
            <FormattedMessage
              {...messages.since}
              values={{
                date: billedSince,
              }}
            />
          </Group>
          {lastBilled !== billedSince && (
            <Group
              centerChildren
              spacing={4}
              wrapperStyle={{color: designColors.headingBlue}}
            >
              <FormattedMessage
                {...messages.last}
                values={{
                  date: lastBilled,
                }}
              />
            </Group>
          )}
        </VerticalGroup>
      </Link>
    </div>
  );
};

export default SubscriptionsPageRecurringDonationPodcast;
